import { motion } from "framer-motion";
import { AlertTriangle, Check, ArrowRight } from "lucide-react";
import { Overline, CTAButton } from "@/components/ui-bits";
import { Reveal, staggerParent, staggerChild } from "@/components/motion-primitives";

const PROBLEMS = [
  "Incorrect billing", "Estimated readings", "Incorrect tariffs", "Unallocated payments",
  "Unexplained adjustments", "Meter discrepancies", "Delays resolving queries",
];

export const ProblemSolution = () => (
  <section className="relative py-24 md:py-32 bg-brand-dark grain overflow-hidden">
    <div className="absolute top-0 right-0 w-1/2 h-full dot-grid opacity-[0.15]" />
    <div className="container mx-auto px-4 md:px-8 relative">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <Reveal><Overline dark>The Problem</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white leading-[1.1]">
              Municipal problems shouldn&rsquo;t consume your time.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed">
              Municipal accounts get complicated &mdash; incorrect billing, estimated readings, wrong tariffs,
              unallocated payments and unexplained adjustments. Instead of chasing different municipal departments,
              SML manages the process on your behalf.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-9">
              <CTAButton href="#quote" variant="secondary" icon={ArrowRight} testId="problem-cta">Get Help With Your Municipal Account</CTAButton>
            </div>
          </Reveal>
        </div>

        <motion.ul variants={staggerParent} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 gap-3">
          {PROBLEMS.map((p, i) => (
            <motion.li
              key={i} variants={staggerChild}
              className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4 hover:border-secondary/40 hover:bg-white/[0.06] transition-colors"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-secondary/15 text-secondary shrink-0 group-hover:bg-secondary group-hover:text-white transition-colors">
                <AlertTriangle className="w-4 h-4" />
              </span>
              <span className="text-sm text-white/85">{p}</span>
            </motion.li>
          ))}
          <motion.li variants={staggerChild} className="sm:col-span-2 flex items-center gap-3 rounded-xl border border-secondary/40 bg-secondary/10 px-4 py-4">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-secondary text-white shrink-0">
              <Check className="w-4 h-4" />
            </span>
            <span className="text-sm font-medium text-white">SML manages it all, on your behalf.</span>
          </motion.li>
        </motion.ul>
      </div>
    </div>
  </section>
);
